import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

import { Mail, Lock, User, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useConsentTracking } from '@/hooks/useConsentTracking';
import { cn } from '@/lib/utils';
import { DOCUMENT_VERSIONS } from '@/lib/constants/documentVersions';
import ForgotPasswordForm from './ForgotPasswordForm';

interface AuthFormProps {
  defaultMode?: 'signin' | 'signup';
  onAuthSuccess?: () => void;
  showForgotPassword?: boolean;
  compact?: boolean;
  /** Called before Google OAuth redirect to allow parent to set flags */
  onBeforeGoogleAuth?: () => void;
}

/** Consent step sub-component for progressive disclosure */
const ConsentStep: React.FC<{
  onAccept: () => void;
  compact?: boolean;
}> = ({ onAccept, compact }) => {
  const handleConsentChange = (checked: boolean) => {
    if (checked) {
      onAccept(); // Immediately reveal auth options
    }
  };

  return (
    <div className={cn("space-y-4", compact && "space-y-3")}>
      <div className="text-center space-y-2">
        <h3 className="font-medium text-foreground">Create an Account</h3>
        <p className="text-sm text-muted-foreground">
          Before you continue, please review our terms.
        </p>
      </div>
      
      <div className="p-4 rounded-lg bg-muted/50 border">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="consent-checkbox"
            onCheckedChange={(c) => handleConsentChange(c as boolean)}
            className="mt-0.5"
          />
          <Label htmlFor="consent-checkbox" className="text-sm font-normal cursor-pointer leading-snug">
            I agree to the{" "}
            <Link 
              to="/terms-of-service" 
              target="_blank"
              className="text-primary hover:underline inline-flex items-center gap-0.5"
            >
              Terms of Service
              <ExternalLink className="h-3 w-3" />
            </Link>
            {" "}and{" "}
            <Link 
              to="/privacy-policy" 
              target="_blank"
              className="text-primary hover:underline inline-flex items-center gap-0.5"
            >
              Privacy Policy
              <ExternalLink className="h-3 w-3" />
            </Link>
          </Label>
        </div>
      </div>
    </div>
  );
};

const AuthForm: React.FC<AuthFormProps> = ({
  defaultMode = 'signin',
  onAuthSuccess,
  showForgotPassword = true,
  compact = false,
  onBeforeGoogleAuth,
}) => {
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'signup');
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  // Track whether user has accepted consent in sign up flow (progressive disclosure)
  const [consentAccepted, setConsentAccepted] = useState(false);
  
  const { signIn, signInWithGoogle, signUp } = useAuth();
  const { logConsent, getClientInfo } = useConsentTracking();

  const handleTabChange = (signup: boolean) => {
    setIsSignUp(signup);
    // Reset consent when switching to sign in (not needed there)
    if (!signup) {
      setConsentAccepted(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Consent is already accepted via progressive disclosure step
    if (isSignUp && !name.trim()) {
      toast.error("Please enter your name.");
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const result = await signUp(email, password, name.trim());
        
        if (result?.error) {
          toast.error(result.error.message);
        } else if (result?.data?.user) {
          // Log split consents (terms + privacy) with their respective versions.
          // Two parallel rows in user_consents create a precise audit trail.
          // Note: cookie consent is NOT logged here — it is collected separately
          // via the cookie banner (TDDDG § 25), and the signup checkbox only
          // covers Terms of Service + Privacy Policy.
          const clientInfo = getClientInfo();
          await Promise.all([
            logConsent({
              consentType: 'terms_acceptance',
              documentVersion: DOCUMENT_VERSIONS.termsOfService,
              userAgent: clientInfo.userAgent,
            }, result.data.user.id),
            logConsent({
              consentType: 'privacy_policy_acceptance',
              documentVersion: DOCUMENT_VERSIONS.privacyPolicy,
              userAgent: clientInfo.userAgent,
            }, result.data.user.id),
          ]);

          // Mark current versions as the implicit baseline so the new user
          // does not see the policy-update banner on next visit.
          try {
            const map = JSON.parse(localStorage.getItem('acknowledgedVersions') || '{}');
            map.termsOfService = DOCUMENT_VERSIONS.termsOfService;
            map.privacyPolicy = DOCUMENT_VERSIONS.privacyPolicy;
            map.cookiePolicy = DOCUMENT_VERSIONS.cookiePolicy;
            localStorage.setItem('acknowledgedVersions', JSON.stringify(map));
          } catch {
            // ignore localStorage failures
          }

          toast.success('Account created successfully! Please check your email to verify your account.');
          onAuthSuccess?.();
        }
      } else {
        await signIn(email, password);
        onAuthSuccess?.();
      }
    } catch (error: any) {
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    // Consent is already accepted via progressive disclosure step
    setLoading(true);
    try {
      // Store consent flag for post-OAuth processing.
      // Persist BOTH terms + privacy versions so Auth.tsx can log split rows
      // matching the email signup flow.
      if (isSignUp) {
        sessionStorage.setItem('pending_consent_google', JSON.stringify({
          termsVersion: DOCUMENT_VERSIONS.termsOfService,
          privacyVersion: DOCUMENT_VERSIONS.privacyPolicy,
          timestamp: new Date().toISOString()
        }));
      }
      
      // Allow parent to set flags before redirect (e.g., pendingUploadSubmit)
      onBeforeGoogleAuth?.();
      
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPasswordForm) {
    return (
      <div className={compact ? '' : 'p-0'}>
        <ForgotPasswordForm onBackToLogin={() => setShowForgotPasswordForm(false)} />
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", compact && "space-y-3")}>
      {/* Tab-style toggle - Sign In first for returning users */}
      <div className="flex border rounded-lg p-1 bg-muted/50">
        <button
          type="button"
          onClick={() => handleTabChange(false)}
          className={cn(
            "flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors",
            !isSignUp 
              ? "bg-background shadow-sm text-foreground" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Sign In
        </button>
        <button
          type="button"
          onClick={() => handleTabChange(true)}
          className={cn(
            "flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors",
            isSignUp 
              ? "bg-background shadow-sm text-foreground" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Sign Up
        </button>
      </div>

      {/* Progressive disclosure: Show consent step first for Sign Up */}
      {isSignUp && !consentAccepted ? (
        <ConsentStep 
          onAccept={() => setConsentAccepted(true)} 
          compact={compact}
        />
      ) : (
        <>
          {/* Show confirmation badge after consent in Sign Up mode */}
          {isSignUp && consentAccepted && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>Terms accepted</span>
              <button 
                type="button"
                onClick={() => setConsentAccepted(false)}
                className="text-primary hover:underline ml-auto text-xs"
              >
                Change
              </button>
            </div>
          )}

          {/* Google OAuth - prominent at top */}
          <Button
            variant="outline"
            onClick={handleGoogleAuth}
            disabled={loading}
            className="w-full"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase text-muted-foreground">
              Or with email
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>
          
          <form onSubmit={handleEmailAuth} className={cn("space-y-4", compact && "space-y-3")}>
            {/* Sign Up: Only Name, Email, Password - Institution/Role collected after */}
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="auth-name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="auth-name"
                    placeholder="Enter your full name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="auth-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="auth-email"
                  placeholder="Enter your email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="auth-password">Password</Label>
                {!isSignUp && showForgotPassword && (
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => setShowForgotPasswordForm(true)}
                    className="p-0 h-auto text-xs"
                  >
                    Forgot Password?
                  </Button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="auth-password"
                  placeholder="Enter your password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default AuthForm;
